import { useEffect, useState } from "react";

import { Loader } from "@/components/Loader";
import { useFeedbackTool } from "@/features/request/feedback-tool/feedbackTool/hooks/useFeedbackTool.tsx";
import { useGetStories } from "@/features/request/feedback-tool/feedbackTool/hooks/useGetStories.tsx";
import { coordinateConvert } from "@/features/request/feedback-tool/feedbackTool/utils/coodinateConvert.ts";
import { Video } from "@/features/request/feedback-tool/feedbackTool/video/components";
import { useGif } from "@/features/request/feedback-tool/feedbackTool/video/hooks/useGif.tsx";

import { checkTimestamp } from "../utils/checkTimestamp";

export default function FeedbackVideo({
  media,
}: {
  media: string | undefined;
}) {
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [duration, setDuration] = useState(0);
  const { formFeedBack, img, setFeedBack, size, videoRef } = useFeedbackTool();
  const video = videoRef.current;
  const { isLoading, mp4Url } = useGif(media);
  const { data } = useGetStories({
    enable: false,
    parentId: img.id,
  });

  useEffect(() => {
    if (!video) return;

    const handleTimeUpdate = () => {
      setCurrentTime(video.currentTime);
      setDuration(video.duration);
    };

    video.addEventListener("timeupdate", handleTimeUpdate);

    return () => {
      video.removeEventListener("timeupdate", handleTimeUpdate);
    };
  }, [video, formFeedBack]);

  useEffect(() => {
    if (!data) {
      setFeedBack(null);

      return;
    }

    setFeedBack(() =>
      data.length > 0
        ? data?.map((feedback, index) => ({
            attachments: feedback?.attachments
              ? feedback?.attachments.map((att) => ({
                  attachmentId: Number(att.attachmentId),
                  name: att.name,
                  storyId: Number(att.storyId),
                  url: att.url,
                }))
              : [],
            commentBatchId: feedback.commentBatchId,
            description: feedback.comment ?? "",
            format: "VIDEO",
            id: index + 1,
            imgId: img.id,
            isResolved: feedback.isResolved ?? false,
            parentId: feedback.parentId,
            storyId: feedback.id,
            timestamp: checkTimestamp(
              Number(coordinateConvert(feedback.coordinates, "timestamp")),
            ),
            updatedAt: feedback.updatedAt,
            user: {
              id: feedback.user?.id,
              name: feedback.user?.name,
            },
            x: coordinateConvert(feedback.coordinates, "y", "video"),
            y: coordinateConvert(feedback.coordinates, "x", "video"),
          }))
        : null,
    );
  }, [data]);

  return (
    <div
      className={`relative flex justify-center items-center ${size === "MODAL" ? "min-h-150 min-w-150" : "max-h-full max-w-full"}`}
    >
      {isLoading ? (
        <Loader className="bg-primary" />
      ) : (
        <Video
          currentTime={currentTime}
          duration={duration}
          key={mp4Url || media}
          media={mp4Url || media}
        />
      )}
    </div>
  );
}
